import { Component } from '@theme/component';

/**
 * The Ritual Letter signup: one form behavior shared by every email signup on
 * the site (snippets/ritual-signup-form.liquid). It sends the email to the
 * Cloudflare Worker (docs/klaviyo-proxy/), which subscribes it in Klaviyo
 * with the form's source label.
 *
 * Also exports the small helpers the home quiz popup and the event page
 * share, so they follow the same rules:
 * - a browser that signed up is never asked again (localStorage)
 * - at most one popup per visit (sessionStorage, so a new tab is a new visit)
 */

const SIGNED_UP_KEY = 'ruie-signed-up';
const POPUP_KEY = 'ruie-popup-shown';

export const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** @returns {boolean} */
export function isSignedUp() {
  try {
    return localStorage.getItem(SIGNED_UP_KEY) === '1';
  } catch {
    return false;
  }
}

/** @returns {boolean} True if no popup has shown this visit and the browser hasn't signed up. */
export function popupAllowed() {
  if (isSignedUp()) return false;
  try {
    return sessionStorage.getItem(POPUP_KEY) !== '1';
  } catch {
    return true;
  }
}

/** Call when a popup opens, so no popup opens again this visit. */
export function claimPopup() {
  try {
    sessionStorage.setItem(POPUP_KEY, '1');
  } catch {
    // Private modes can refuse storage. Worst case a second popup shows.
  }
}

function markSignedUp() {
  try {
    localStorage.setItem(SIGNED_UP_KEY, '1');
  } catch {
    // Without storage the browser may be asked again on a later visit.
  }
}

/**
 * Sends one signup to the Worker. Throws if the Worker didn't accept it.
 * Resolves to true when the email was already subscribed.
 *
 * @param {object} signup
 * @param {string} signup.endpoint - Worker URL
 * @param {string} signup.email
 * @param {string} signup.source - e.g. quiz, footer, event
 * @param {string} [signup.fabric] - quiz result
 * @param {string} [signup.honeypot] - value of the hidden bot field
 */
export async function subscribe({ endpoint, email, source, fabric, honeypot }) {
  const response = await fetch(endpoint, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, source, fabric: fabric || undefined, company: honeypot || '' }),
  });
  const data = await response.json().catch(() => null);
  if (!response.ok || !data?.ok) {
    throw new Error(`Signup failed: ${response.status}`);
  }
  markSignedUp();
  return data.already === true;
}

/**
 * @typedef {object} RitualSignupRefs
 * @property {HTMLFormElement} form
 * @property {HTMLInputElement} email
 * @property {HTMLButtonElement} button
 * @property {HTMLInputElement} honeypot
 * @property {HTMLElement} error
 * @property {HTMLElement} success
 */

/** @extends {Component<RitualSignupRefs>} */
class RitualSignup extends Component {
  requiredRefs = ['form', 'email', 'button', 'honeypot', 'error', 'success'];

  /** @param {SubmitEvent} event */
  async submit(event) {
    event.preventDefault();
    const { email, button, honeypot, form, success } = this.refs;
    const value = email.value.trim();

    if (!EMAIL_PATTERN.test(value)) {
      this.#showError('Please enter a valid email address.');
      email.focus();
      return;
    }

    this.#showError('');
    button.disabled = true;
    this.setAttribute('aria-busy', 'true');

    try {
      const already = await subscribe({
        endpoint: this.dataset.endpoint ?? '',
        email: value,
        source: this.dataset.source ?? 'other',
        fabric: this.dataset.fabric,
        honeypot: honeypot.value,
      });
      if (already && this.dataset.already) success.textContent = this.dataset.already;
      form.hidden = true;
      success.hidden = false;
      success.focus();
      this.#hideOtherSurfaces();
      this.dispatchEvent(new CustomEvent('ritual:success', { bubbles: true }));
    } catch {
      this.#showError(this.dataset.error ?? '');
      button.disabled = false;
    } finally {
      this.removeAttribute('aria-busy');
    }
  }

  /** @param {string} message */
  #showError(message) {
    const { error, email } = this.refs;
    error.textContent = message;
    error.hidden = !message;
    email.toggleAttribute('aria-invalid', Boolean(message));
  }

  // One signup is enough. Every other offer on the page goes away, but the
  // surface holding this form stays so the success message can be read. The
  // class (not a per-element flag) keeps them hidden even when a section
  // re-renders.
  #hideOtherSurfaces() {
    this.closest('[data-rs-offer]')?.setAttribute('data-rs-keep', '');
    document.documentElement.classList.add('rs-signed-up');
  }
}

if (!customElements.get('ritual-signup')) {
  customElements.define('ritual-signup', RitualSignup);
}
